import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface Message {
  id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  is_read: boolean;
  created_at: string;
  sender_profile?: {
    display_name: string | null;
    avatar_url: string | null;
  };
}

interface Conversation {
  id: string;
  user1_id: string;
  user2_id: string;
  last_message_at: string;
  other_user: {
    id: string;
    display_name: string | null;
    avatar_url: string | null;
  };
  unread_count: number;
  last_message?: string;
}

export function useMessaging() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeConversation, setActiveConversation] = useState<string | null>(null);

  const fetchConversations = useCallback(async () => {
    if (!user) return;
    
    const { data: convos } = await supabase
      .from('conversations')
      .select('*')
      .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
      .order('last_message_at', { ascending: false });

    if (convos) {
      const enriched: Conversation[] = await Promise.all(convos.map(async (conv) => {
        const otherId = conv.user1_id === user.id ? conv.user2_id : conv.user1_id;
        
        // Use public_profiles view for viewing other users' profiles
        const { data: profile } = await supabase
          .from('public_profiles' as any)
          .select('id, display_name, avatar_url')
          .eq('id', otherId)
          .single() as { data: { id: string; display_name: string | null; avatar_url: string | null } | null };

        const { count } = await supabase
          .from('direct_messages')
          .select('*', { count: 'exact', head: true })
          .eq('receiver_id', user.id)
          .eq('sender_id', otherId)
          .eq('is_read', false);

        const { data: lastMsg } = await supabase
          .from('direct_messages')
          .select('content')
          .or(`and(sender_id.eq.${user.id},receiver_id.eq.${otherId}),and(sender_id.eq.${otherId},receiver_id.eq.${user.id})`)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        return {
          ...conv,
          other_user: profile || { id: otherId, display_name: null, avatar_url: null },
          unread_count: count || 0,
          last_message: lastMsg?.content
        };
      }));
      
      setConversations(enriched);
    }
    setLoading(false);
  }, [user]);

  const fetchMessages = useCallback(async (otherUserId: string) => {
    if (!user) return;
    
    setActiveConversation(otherUserId);
    
    const { data } = await supabase
      .from('direct_messages')
      .select('*')
      .or(`and(sender_id.eq.${user.id},receiver_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},receiver_id.eq.${user.id})`)
      .order('created_at', { ascending: true });

    if (data) {
      // Mark as read
      await supabase
        .from('direct_messages')
        .update({ is_read: true })
        .eq('sender_id', otherUserId)
        .eq('receiver_id', user.id)
        .eq('is_read', false);

      setMessages(data);
    }
  }, [user]);

  const sendMessage = async (receiverId: string, content: string) => {
    if (!user || !content.trim()) return;

    const { error } = await supabase
      .from('direct_messages')
      .insert({
        sender_id: user.id,
        receiver_id: receiverId,
        content: content.trim()
      });

    if (error) {
      console.error('Error sending message:', error);
      return false;
    }

    // Update or create conversation
    const [id1, id2] = [user.id, receiverId].sort();
    const { data: existing } = await supabase
      .from('conversations')
      .select('id')
      .eq('user1_id', id1)
      .eq('user2_id', id2)
      .single();

    if (existing) {
      await supabase
        .from('conversations')
        .update({ last_message_at: new Date().toISOString() })
        .eq('id', existing.id);
    } else {
      await supabase
        .from('conversations')
        .insert({
          user1_id: id1,
          user2_id: id2,
          last_message_at: new Date().toISOString()
        });
    }

    await fetchMessages(receiverId);
    await fetchConversations();
    return true;
  };

  const startConversation = async (userId: string) => {
    setActiveConversation(userId);
    await fetchMessages(userId);
  };

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Real-time subscription
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('dm-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'direct_messages',
          filter: `receiver_id=eq.${user.id}`
        },
        () => {
          fetchConversations();
          if (activeConversation) {
            fetchMessages(activeConversation);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, activeConversation, fetchConversations, fetchMessages]);

  return {
    conversations,
    messages,
    loading,
    activeConversation,
    fetchMessages,
    sendMessage,
    startConversation,
    setActiveConversation
  };
}
